import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { MessageSquare, ChevronDown } from "lucide-react";

const CATS = [
  { key: "general", label: "General", color: "#1E6FE6" },
  { key: "technical", label: "Technical", color: "#0EA5E9" },
  { key: "behavioral", label: "Behavioral", color: "#10B981" },
  { key: "company_specific", label: "Company-specific", color: "#F59E0B" },
];

const LEVELS = ["Entry", "Mid", "Senior", "Lead"];

export default function InterviewPractice() {
  const [resumes, setResumes] = useState([]);
  const [form, setForm] = useState({ resume_id: "", job_title: "", company: "", job_description: "", experience_level: "Mid" });
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/resumes").then((r) => {
      setResumes(r.data || []);
      if (r.data?.[0]) setForm((f) => ({ ...f, resume_id: r.data[0].resume_id }));
    });
  }, []);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.job_title || !form.company || !form.job_description) return toast.error("Fill all fields");
    setBusy(true);
    setData(null);
    try {
      const r = await api.post("/interview", form);
      const total = (r.data?.general?.length || 0) + (r.data?.technical?.length || 0)
        + (r.data?.behavioral?.length || 0) + (r.data?.company_specific?.length || 0);
      if (total === 0) {
        toast.error("AI did not return any questions. Please try again.");
      } else {
        setData(r.data);
        toast.success(`${total} questions ready`);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Generation failed";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-8">
      <div className="fade-up">
        <p className="text-xs font-bold uppercase tracking-wider text-[#1E6FE6]">Interview practice</p>
        <h1 className="font-display font-black text-4xl tracking-tight mt-1">Rehearse what they&apos;ll ask</h1>
        <p className="text-slate-500 mt-2 max-w-xl">12 general + 12 technical + 12 behavioral + 5 company-specific questions, each with a sample answer.</p>
      </div>

      <form onSubmit={submit} className="glass p-6 fade-up-delay-1 grid lg:grid-cols-2 gap-5">
        <label className="block">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Resume</span>
          <select
            data-testid="iv-resume"
            value={form.resume_id}
            onChange={(e) => setForm({ ...form, resume_id: e.target.value })}
            className="mt-1.5 w-full rounded-xl border border-white/10 focus:border-[#00E5FF] outline-none px-3 py-2.5 text-sm bg-white/5 text-white"
          >
            <option value="">— None —</option>
            {resumes.map((r) => <option key={r.resume_id} value={r.resume_id}>{r.filename}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Experience level</span>
          <select
            data-testid="iv-level"
            value={form.experience_level}
            onChange={(e) => setForm({ ...form, experience_level: e.target.value })}
            className="mt-1.5 w-full rounded-xl border border-white/10 focus:border-[#00E5FF] outline-none px-3 py-2.5 text-sm bg-white/5 text-white"
          >
            {LEVELS.map((l) => <option key={l}>{l}</option>)}
          </select>
        </label>
        <Field label="Job title" value={form.job_title} onChange={(v) => setForm({ ...form, job_title: v })} testid="iv-title" />
        <Field label="Company" value={form.company} onChange={(v) => setForm({ ...form, company: v })} testid="iv-company" />
        <div className="lg:col-span-2">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Job description</span>
          <textarea
            data-testid="iv-jd"
            value={form.job_description}
            onChange={(e) => setForm({ ...form, job_description: e.target.value })}
            rows={6}
            className="mt-1.5 w-full rounded-xl border-2 border-white/10 focus:border-[#1E6FE6] outline-none px-3.5 py-3 text-sm font-mono"
          />
        </div>
        <div className="lg:col-span-2 flex justify-end">
          <button
            data-testid="iv-submit"
            type="submit"
            disabled={busy}
            className="btn-neon inline-flex items-center gap-2 disabled:opacity-60"
          >
            <MessageSquare size={16} /> {busy ? "Generating…" : "Generate questions"}
          </button>
        </div>
      </form>

      {data && (
        <div className="space-y-8 fade-up-delay-2">
          {CATS.map((c) => (
            <div key={c.key}>
              <div className="flex items-center gap-2 mb-3">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: c.color }} />
                <h2 className="font-display font-bold text-xl">{c.label}</h2>
                <span className="text-xs text-slate-500">({(data[c.key] || []).length})</span>
              </div>
              <div className="space-y-3">
                {(data[c.key] || []).map((q, i) => <QA key={i} q={q} idx={i} color={c.color} />)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function QA({ q, idx, color }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="glass overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        data-testid={`iv-qa-${idx}`}
        className="w-full px-5 py-4 flex items-center justify-between gap-4 text-left"
      >
        <div className="flex items-start gap-3 min-w-0">
          <span
            className="grid place-items-center w-7 h-7 rounded-lg text-xs font-bold shrink-0 mt-0.5"
            style={{ background: `${color}1A`, color }}
          >
            {idx + 1}
          </span>
          <span className="font-semibold">{q.question}</span>
        </div>
        <ChevronDown size={18} className={`shrink-0 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-5 pb-5 pt-1 border-t border-white/8">
          {q.answer_structure && (
            <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mt-3">Structure</div>
          )}
          {q.answer_structure && <p className="text-sm text-slate-300 mt-1">{q.answer_structure}</p>}
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mt-3">Sample answer</div>
          <p className="text-sm text-slate-200 mt-1 leading-relaxed whitespace-pre-line">{q.sample_answer}</p>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</span>
      <input
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1.5 w-full rounded-xl border-2 border-white/10 focus:border-[#1E6FE6] outline-none px-3.5 py-2.5 text-sm"
      />
    </label>
  );
}
